import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faSpinner,
  faTriangleExclamation,
} from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import { CC_PANEL } from "@/components/case-checking/caseCheckingChrome";

export function CompletenessAgentStatusBanner({
  agentCall,
  missingLabels,
  agentSummary,
}: {
  agentCall: "complete" | "incomplete" | "pending";
  missingLabels: string[];
  /** API summary when checklist rows do not list the gap (single combined banner). */
  agentSummary?: string | null;
}) {
  if (agentCall === "pending") {
    return (
      <div
        className={clsx(CC_PANEL, "flex items-center gap-3 border-l-4 border-l-slate-400 py-3")}
        data-testid="completeness-agent-banner"
      >
        <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin text-slate-400" />
        <p className="text-sm text-slate-600 dark:text-slate-300">Completeness check still running…</p>
      </div>
    );
  }

  if (agentCall === "complete") {
    return (
      <div
        className={clsx(
          CC_PANEL,
          "border-l-4 border-l-emerald-500/80 bg-emerald-500/5 py-3 dark:bg-emerald-500/10",
        )}
        data-testid="completeness-agent-banner"
      >
        <div className="flex items-start gap-3">
          <FontAwesomeIcon icon={faCircleCheck} className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <div>
            <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">Agent: file complete</p>
            <p className="mt-0.5 text-sm text-emerald-800/90 dark:text-emerald-200/90">
              Required documents are on file.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        CC_PANEL,
        "border-l-4 border-l-rose-500/90 bg-rose-500/10 py-3 ring-1 ring-rose-500/20 dark:bg-rose-500/15",
      )}
      data-testid="completeness-agent-banner"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <FontAwesomeIcon
          icon={faTriangleExclamation}
          className="mt-0.5 h-5 w-5 shrink-0 text-rose-600 dark:text-rose-400"
        />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-rose-900 dark:text-rose-100">
            Agent: file incomplete
            {missingLabels.length > 0 ? ` — ${missingLabels.length} required item${missingLabels.length === 1 ? "" : "s"} missing` : ""}
          </p>
          {missingLabels.length > 0 ? (
            <ul className="mt-2 list-disc space-y-0.5 pl-4 text-sm text-rose-900/90 dark:text-rose-100/90">
              {missingLabels.slice(0, 8).map((label) => (
                <li key={label}>{label}</li>
              ))}
              {missingLabels.length > 8 ? (
                <li className="list-none pl-0 text-rose-800/80 dark:text-rose-200/80">
                  + {missingLabels.length - 8} more in the checklist below
                </li>
              ) : null}
            </ul>
          ) : agentSummary?.trim() ? (
            <p className="mt-1.5 text-sm text-rose-900/90 dark:text-rose-100/90">{agentSummary.trim()}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
